
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectorRef, Component, DoCheck, HostBinding, forwardRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { IconDef } from '../icons/icon';
import { InputReusable } from '../input-reusable/input-reusable';
import { SelectComponent } from '../select/select';

interface CraftyAddressResponse {
  error_code?: string;
  town?: string;
  postal_town?: string;
  postal_county?: string;
  traditional_county?: string;
  country?: string;
  delivery_points?: Array<{
    organisation_name?: string;
    department_name?: string;
    line_1?: string;
    line_2?: string;
  }>;
}

/**
 * Granular ("response=data") variant of the RapidAddress payload. Only requested
 * when a consumer opts in by wiring up the address-component controls, so the
 * default lookup for every other consumer stays on the formatted response.
 *
 * Delivery points are nested under thoroughfares; the street lives on the
 * thoroughfare and the locality/town/county live at the top level.
 */
interface CraftyAddressDataResponse {
  error_code?: string;
  town?: string;
  postal_county?: string;
  traditional_county?: string;
  dependent_locality?: string;
  double_dependent_locality?: string;
  /** Constituent country (England/Scotland/Wales/Northern Ireland), when the API supplies it. */
  country?: string;
  thoroughfares?: Array<{
    dependent_thoroughfare_name?: string;
    dependent_thoroughfare_descriptor?: string;
    thoroughfare_name?: string;
    thoroughfare_descriptor?: string;
    delivery_points?: Array<{
      organisation_name?: string;
      department_name?: string;
      po_box_number?: string;
      building_number?: string;
      sub_building_name?: string;
      building_name?: string;
    }>;
  }>;
}

/** Broken-down premise fields for an individual address. */
interface AddressComponents {
  /** Sub building name, e.g. "Flat 2". */
  flat: string;
  /** Building name, e.g. "Miles Suite". */
  building: string;
  /** Building number / door number, e.g. "12". */
  door: string;
  /** Thoroughfare / street, e.g. "High Road". */
  street: string;
  /** Postal/traditional county, e.g. "Surrey". */
  county: string;
  /** Post town, e.g. "London". */
  town: string;
  /** Not supplied by the lookup — always blank; consumers capture it manually. */
  region: string;
  /** Constituent country, e.g. "England" — only populated when the API returns one. */
  constituentCountry: string;
}

interface NormalizedAddressOption {
  formatted: string;
  components: AddressComponents | null;
}

interface NormalizedLookup {
  error: boolean;
  city: string;
  country: string;
  options: NormalizedAddressOption[];
}

@Component({
  selector: 'app-postcode-reusable',
  standalone: true,
  imports: [ReactiveFormsModule, InputReusable, SelectComponent],
  templateUrl: './postcode-reusable.html',
  styleUrl: './postcode-reusable.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PostcodeReusable),
      multi: true,
    },
  ],
})
export class PostcodeReusable implements OnInit, OnDestroy, DoCheck, ControlValueAccessor {
  @HostBinding('class.inline-layout') get inlineLayoutClass(): boolean {
    return this.inlineLayout;
  }

  @Input() controlOnly = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'lg';
  @Input() label = 'Post Code';
  @Input() placeholder = 'e.g. TW89DW';
  @Input() required = false;
  @Input() externalErrorMessage = '';
  @Input() addressLabel = 'Address';
  @Input() addressPlaceholder = 'Address';
  @Input() cityLabel = 'City';
  @Input() cityPlaceholder = 'City';
  @Input() countryLabel = 'Country';
  @Input() countryPlaceholder = 'Country';
  @Input() postcodeControl: FormControl<string | number | null> | null = null;
  @Input() addressControl: FormControl<string | number | null> | null = null;
  @Input() cityControl: FormControl<string | number | null> | null = null;
  @Input() countryControl: FormControl<string | number | null> | null = null;

  // Optional address-component controls. Passing any of these opts the lookup
  // into the granular ("response=data") mode and populates them on selection.
  // Left null for every other consumer, so their behaviour is unchanged.
  @Input() buildingControl: FormControl<string | number | null> | null = null;
  @Input() flatControl: FormControl<string | number | null> | null = null;
  @Input() streetControl: FormControl<string | number | null> | null = null;
  @Input() doorControl: FormControl<string | number | null> | null = null;
  @Input() countyControl: FormControl<string | number | null> | null = null;
  @Input() townControl: FormControl<string | number | null> | null = null;
  @Input() regionControl: FormControl<string | number | null> | null = null;
  @Input() constituentCountryControl: FormControl<string | number | null> | null = null;
  @Input() countryOptions: Array<{ label: string; value: string } | string> = [];
  @Input() useAddressDropdown = true;
  @Input() enableLookup = true;
  @Input() showLookupFeedback = true;
  @Input() showPostcodeField = true;
  @Input() showAddressField = true;
  @Input() showCityField = true;
  @Input() showCountryField = true;
  @Input() inlineLayout = false;

  @Input()
  set sameAsCompanyAddress(value: boolean) {
    this._sameAsCompanyAddress = value === true;
    this.form.get('sameAsCompanyAddress')?.setValue(this._sameAsCompanyAddress, { emitEvent: false });
  }
  get sameAsCompanyAddress(): boolean {
    return this._sameAsCompanyAddress;
  }

  private readonly fb = inject(FormBuilder);
  private readonly backend = inject(HttpBackend);
  private readonly rawHttp = new HttpClient(this.backend);
  private readonly cdr = inject(ChangeDetectorRef);
  private _sameAsCompanyAddress = false;

  readonly form = this.fb.group({
    sameAsCompanyAddress: [false],
    postcode: ['', [Validators.required]],
    address: ['', [Validators.required]],
    city: ['', [Validators.required]],
    country: ['United Kingdom', [Validators.required]],
  });

  readonly externalPostcodeControl = new FormControl<string | number | null>('');

  availableAddresses: string[] = [];
  private availableAddressComponents: Array<AddressComponents | null> = [];
  isLoading = false;
  lookupError = '';

  /** Only true when a consumer wires up at least one address-component control. */
  private get captureAddressComponents(): boolean {
    return !!(
      this.buildingControl ||
      this.flatControl ||
      this.streetControl ||
      this.doorControl ||
      this.countyControl ||
      this.townControl ||
      this.regionControl ||
      this.constituentCountryControl
    );
  }

  private readonly destroy$ = new Subject<void>();
  private readonly boundExternalControls = new WeakSet<FormControl<string | number | null>>();
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnInit(): void {
    this.setupPostcodeLookup();
    this.setupAddressSelection();
    this.setupExternalPostcodeControl();
    this.setupLegacyExternalBindings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngDoCheck(): void {
    this.setupLegacyExternalBindings();
    this.syncExternalValuesFromInputs();
  }

  writeValue(value: unknown): void {
    this.externalPostcodeControl.setValue(String(value ?? ''), { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.externalPostcodeControl.disable({ emitEvent: false });
      return;
    }

    this.externalPostcodeControl.enable({ emitEvent: false });
  }

  markAsTouched(): void {
    this.onTouched();
  }

  /**
   * Fetches candidate addresses for an already-known postcode (e.g. right after hydrating
   * from a GET call, or after a save response) without touching the current address value.
   * Only populates `availableAddresses` — and so only surfaces the dropdown — when the
   * postcode actually resolves to more than one address; a single/no match leaves the
   * plain address input in place.
   */
  loadAddressOptionsForPostcode(postcode: string | null | undefined, currentAddress?: string | null): void {
    const trimmed = String(postcode ?? '').trim();
    const stripped = trimmed.replace(/\s/g, '');
    if (!stripped || stripped.length < 5) {
      return;
    }

    this.lookupAddressByPostcode(trimmed.toUpperCase())
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (!result || result.error || result.options.length <= 1) {
          return;
        }

        let formatted = result.options.map((option) => option.formatted);
        let components = result.options.map((option) => option.components);

        // Guarantee the already-saved address is selectable even if its stored
        // formatting doesn't exactly match the lookup's formatted text.
        const savedAddress = (currentAddress ?? '').trim();
        if (savedAddress && !formatted.includes(savedAddress)) {
          formatted = [savedAddress, ...formatted];
          components = [null, ...components];
        }

        this.availableAddresses = formatted;
        this.availableAddressComponents = components;
        this.cdr.markForCheck();
      });
  }

  get fieldGridClass(): string {
    return this.visibleFieldCount <= 1
      ? 'grid grid-cols-1 gap-4'
      : 'grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5';
  }

  private get visibleFieldCount(): number {
    return [
      this.showPostcodeField,
      this.showAddressField,
      this.showCityField,
      this.showCountryField,
    ].filter(Boolean).length;
  }

  private setupExternalPostcodeControl(): void {
    this.externalPostcodeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onChange(String(value ?? ''));
    });
  }

  private setupLegacyExternalBindings(): void {
    this.bindExternalControl('postcode', this.postcodeControl);
    this.bindExternalControl('address', this.addressControl);
    this.bindExternalControl('city', this.cityControl);
    this.bindExternalControl('country', this.countryControl);
  }

  private bindExternalControl(
    internalControlName: 'postcode' | 'address' | 'city' | 'country',
    externalControl: FormControl<string | number | null> | null,
  ): void {
    if (!externalControl) {
      return;
    }

    const internalControl = this.form.get(internalControlName) as FormControl<string | number | null> | null;
    if (!internalControl) {
      return;
    }

    const externalValue = externalControl.value;
    if (externalValue !== null && externalValue !== undefined && externalValue !== '') {
      const normalizedValue = this.normalizeControlValue(internalControlName, externalValue);
      if (internalControl.value !== normalizedValue) {
        internalControl.setValue(normalizedValue, { emitEvent: false });
      }
    } else {
      externalControl.setValue(internalControl.value, { emitEvent: false });
    }

    if (!this.boundExternalControls.has(externalControl)) {
      externalControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        const normalizedValue = this.normalizeControlValue(internalControlName, value);
        if (internalControl.value !== normalizedValue) {
          internalControl.setValue(normalizedValue, {
            emitEvent: internalControlName === 'postcode' && this.enableLookup,
          });
        }
      });

      internalControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        if (externalControl.value !== value) {
          externalControl.setValue(value);
        }
      });

      this.boundExternalControls.add(externalControl);
    }
  }

  private syncExternalValuesFromInputs(): void {
    this.syncExternalValueToInternal('postcode', this.postcodeControl);
    this.syncExternalValueToInternal('address', this.addressControl);
    this.syncExternalValueToInternal('city', this.cityControl);
    this.syncExternalValueToInternal('country', this.countryControl);
  }

  private syncExternalValueToInternal(
    internalControlName: 'postcode' | 'address' | 'city' | 'country',
    externalControl: FormControl<string | number | null> | null,
  ): void {
    if (!externalControl) {
      return;
    }

    const internalControl = this.form.get(internalControlName) as FormControl<string | number | null> | null;
    if (!internalControl) {
      return;
    }

    const normalizedValue = this.normalizeControlValue(internalControlName, externalControl.value);
    if (internalControl.value !== normalizedValue) {
      internalControl.setValue(normalizedValue, { emitEvent: false });
    }
  }

  /** Postcodes are always displayed/stored uppercase with the standard space, regardless of entry path. */
  private normalizeControlValue(
    controlName: 'postcode' | 'address' | 'city' | 'country',
    value: string | number | null,
  ): string | number | null {
    if (controlName !== 'postcode' || value === null || value === undefined) {
      return value;
    }
    return this.formatUKPostcode(String(value));
  }

  private setupPostcodeLookup(): void {
    const postcodeControl = this.form.get('postcode');
    if (!postcodeControl) {
      return;
    }

    postcodeControl.valueChanges
      .pipe(
        map((rawValue) => this.formatUKPostcode(String(rawValue ?? ''))),
        tap((formatted) => {
          // Insert the space live as the user types, without re-triggering this
          // same pipeline (emitEvent: false keeps the debounce/lookup below driven
          // by a single emission per keystroke).
          if (postcodeControl.value !== formatted) {
            postcodeControl.setValue(formatted, { emitEvent: false });
          }
        }),
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => {
          this.lookupError = '';
        }),
        switchMap((formattedValue) => {
          const postcode = String(formattedValue || '')
            .trim()
            .toUpperCase();
          const stripped = postcode.replace(/\s/g, '');
          const sameAsCompanyAddress = this.form.get('sameAsCompanyAddress')?.value === true;

          // If the postcode is invalid or empty, clear address options.
          if (!stripped || stripped.length < 5 || postcodeControl.invalid) {
            this.clearAddressOptions();
            this.form.patchValue({ city: '' });
            return of(null);
          }

          // If the form is marked as 'same as company address' we should not
          // perform a postcode lookup nor clear any existing address value
          // (the external form will already have the company address).
          if (sameAsCompanyAddress) {
            return of(null);
          }

          this.isLoading = true;
          this.cdr.markForCheck();
          return this.lookupAddressByPostcode(postcode).pipe(
            finalize(() => {
              this.isLoading = false;
              this.cdr.markForCheck();
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        if (!result) {
          this.cdr.markForCheck();
          return;
        }

        if (result.error) {
          this.clearAddressOptions();
          this.form.patchValue({ city: '' });
          this.lookupError = 'No addresses found for this postcode.';
          this.cdr.markForCheck();
          return;
        }

        this.availableAddresses = result.options.map((option) => option.formatted);
        this.availableAddressComponents = result.options.map((option) => option.components);

        const patch: Record<string, string> = {};

        // Default to the first match — whether there's a single premise or several.
        // When there are multiple, the dropdown (handled by setupAddressSelection)
        // stays available so the user can pick a different one.
        if (result.options.length > 0) {
          patch['address'] = result.options[0].formatted;
          this.applyAddressComponents(result.options[0].components);
        } else {
          patch['address'] = '';
          this.applyAddressComponents(null);
        }

        patch['city'] = result.city || '';

        if (result.country) {
          patch['country'] = result.country;
        }

        this.form.patchValue(patch);
        this.cdr.markForCheck();
      });
  }

  /**
   * When the granular controls are wired up, keep the address-component fields in
   * sync with whichever address is picked. We watch the internal control (for the
   * reusable's own dropdown) and, if provided, the external address control — some
   * consumers render their own address dropdown bound to that external control,
   * and the internal→external sync runs with emitEvent:false.
   */
  private setupAddressSelection(): void {
    this.form
      .get('address')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.applyComponentsForAddress(value));

    this.addressControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.applyComponentsForAddress(value));
  }

  private applyComponentsForAddress(value: unknown): void {
    if (!this.captureAddressComponents) {
      return;
    }

    const index = this.availableAddresses.indexOf(String(value ?? ''));
    if (index >= 0) {
      this.applyAddressComponents(this.availableAddressComponents[index] ?? null);
    }
  }

  /** Push (or clear) the broken-down premise fields onto the external controls. */
  private applyAddressComponents(components: AddressComponents | null): void {
    if (!this.captureAddressComponents) {
      return;
    }

    this.buildingControl?.setValue(components?.building ?? '');
    this.flatControl?.setValue(components?.flat ?? '');
    this.streetControl?.setValue(components?.street ?? '');
    this.doorControl?.setValue(components?.door ?? '');
    this.countyControl?.setValue(components?.county ?? '');
    this.townControl?.setValue(components?.town ?? '');
    this.constituentCountryControl?.setValue(components?.constituentCountry ?? '');
    // regionControl is intentionally left alone — the lookup never supplies a region,
    // so auto-resetting it here would wipe out whatever the consumer typed manually.
  }

  private normalizeCountry(country: string): string {
    const value = String(country || '')
      .trim()
      .toLowerCase();
    if (
      value === 'england' ||
      value === 'scotland' ||
      value === 'wales' ||
      value === 'northern ireland'
    ) {
      return 'United Kingdom';
    }
    return country || 'United Kingdom';
  }

  /** RapidAddress `data` returns UPPERCASE values; present them in title case. */
  private toTitleCase(value: string): string {
    return String(value || '')
      .toLowerCase()
      .replace(/\b[a-z]/g, (char) => char.toUpperCase());
  }

  private formatUKPostcode(raw: string): string {
    const clean = raw.replace(/\s/g, '').toUpperCase();
    if (clean.length >= 5 && clean.length <= 7) {
      return `${clean.slice(0, -3)} ${clean.slice(-3)}`;
    }
    return clean;
  }

  private lookupAddressByPostcode(postcode: string) {
    return this.captureAddressComponents
      ? this.lookupGranularAddress(postcode)
      : this.lookupFormattedAddress(postcode);
  }

  /** Default lookup (formatted single-line addresses) — unchanged behaviour. */
  private lookupFormattedAddress(postcode: string) {
    const payload = {
      key: '3d776-8d063-700fe-07aa9',
      postcode: this.formatUKPostcode(postcode),
      response: 'data_formatted',
    };

    return this.rawHttp
      .post<CraftyAddressResponse>('https://pcls1.craftyclicks.co.uk/json/rapidaddress', payload, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(
        map((response): NormalizedLookup => {
          if (response?.error_code) {
            return { error: true, city: '', country: '', options: [] };
          }

          const city = response.town || response.postal_town || '';
          const county = response.postal_county || response.traditional_county || '';
          const country = this.normalizeCountry(response.country || 'United Kingdom');
          const deliveryPoints = Array.isArray(response.delivery_points)
            ? response.delivery_points
            : [];

          const options = this.dedupeOptions(
            deliveryPoints
              .map((item) => {
                // line_1/line_2 are frequently identical across every unit in a shared
                // building (e.g. an office block) — organisation_name is what actually
                // distinguishes one delivery point from another in that case.
                const formatted = [
                  item?.organisation_name,
                  item?.department_name,
                  item?.line_1,
                  item?.line_2,
                  city,
                  county,
                ]
                  .filter(Boolean)
                  .join(', ');
                return { formatted, components: null } as NormalizedAddressOption;
              })
              .filter((option) => !!option.formatted),
          );

          return { error: false, city, country, options };
        }),
        catchError(() => {
          this.lookupError = 'Unable to fetch addresses right now.';
          this.clearAddressOptions();
          return of<NormalizedLookup>({ error: true, city: '', country: '', options: [] });
        }),
      );
  }

  /** Granular lookup (broken-down premise fields) — opt-in per consumer. */
  private lookupGranularAddress(postcode: string) {
    const payload = {
      key: '3d776-8d063-700fe-07aa9',
      postcode: this.formatUKPostcode(postcode),
      response: 'data',
    };

    return this.rawHttp
      .post<CraftyAddressDataResponse>(
        'https://pcls1.craftyclicks.co.uk/json/rapidaddress',
        payload,
        { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) },
      )
      .pipe(
        map((response): NormalizedLookup => {
          if (response?.error_code) {
            return { error: true, city: '', country: '', options: [] };
          }

          const city = this.toTitleCase(response.town || '');
          const county = this.toTitleCase(
            response.postal_county || response.traditional_county || '',
          );
          const locality = this.toTitleCase(response.dependent_locality || '');
          const country = 'United Kingdom';
          // Best-effort: only some RapidAddress plans return a constituent country on
          // the granular ("data") response. Left blank when absent — captured manually.
          const constituentCountry = this.toTitleCase(response.country || '');
          const thoroughfares = Array.isArray(response.thoroughfares)
            ? response.thoroughfares
            : [];

          const rawOptions: NormalizedAddressOption[] = [];

          for (const thoroughfare of thoroughfares) {
            const street = this.toTitleCase(
              [thoroughfare?.thoroughfare_name, thoroughfare?.thoroughfare_descriptor]
                .filter(Boolean)
                .join(' '),
            );
            const dependentStreet = this.toTitleCase(
              [
                thoroughfare?.dependent_thoroughfare_name,
                thoroughfare?.dependent_thoroughfare_descriptor,
              ]
                .filter(Boolean)
                .join(' '),
            );
            const streetLabel = [dependentStreet, street].filter(Boolean).join(', ');
            const deliveryPoints = Array.isArray(thoroughfare?.delivery_points)
              ? thoroughfare.delivery_points
              : [];

            for (const point of deliveryPoints) {
              const organisation = this.toTitleCase(point?.organisation_name || '');
              // Some premises (e.g. named buildings) have no PAF building name and
              // only carry an organisation name — fall back to it so the Building
              // field is populated instead of left blank.
              const building = this.toTitleCase(point?.building_name || '') || organisation;

              const components: AddressComponents = {
                flat: this.toTitleCase(point?.sub_building_name || ''),
                building,
                door: point?.building_number || '',
                street: streetLabel,
                county,
                town: city,
                region: '',
                constituentCountry,
              };

              const premise = [components.flat, building].filter(Boolean).join(', ');
              const streetLine = [components.door, streetLabel].filter(Boolean).join(' ');
              // Don't repeat the organisation when it's already shown as the building.
              const leadingOrganisation =
                organisation && organisation !== building ? organisation : '';
              const formatted = [leadingOrganisation, premise, streetLine, locality, city, county]
                .filter(Boolean)
                .join(', ');

              if (formatted) {
                rawOptions.push({ formatted, components });
              }
            }
          }

          return { error: false, city, country, options: this.dedupeOptions(rawOptions) };
        }),
        catchError(() => {
          this.lookupError = 'Unable to fetch addresses right now.';
          this.clearAddressOptions();
          return of<NormalizedLookup>({ error: true, city: '', country: '', options: [] });
        }),
      );
  }

  /** Drop duplicate formatted addresses while keeping the first occurrence. */
  private dedupeOptions(options: NormalizedAddressOption[]): NormalizedAddressOption[] {
    const seen = new Set<string>();
    return options.filter((option) => {
      if (seen.has(option.formatted)) {
        return false;
      }
      seen.add(option.formatted);
      return true;
    });
  }

  private clearAddressOptions(): void {
    this.availableAddresses = [];
    this.availableAddressComponents = [];
    this.isLoading = false;
    this.form.patchValue({ address: '' });
    this.applyAddressComponents(null);
    this.cdr.markForCheck();
  }
}
